import type { CSSProperties } from 'react';
import type {
  AnyObjectSchema,
  Conjunction,
  ElementOf,
  InferArgFromValidatorList,
  InferArgType,
  InferFnArgs,
  InferTypeFromSchema,
  KeyboardEventKeys,
  Optional,
  Prettify,
  ValidateFnArgs,
  Validator,
} from './types';

export const bool = (): Validator<boolean> => (arg) => typeof arg === 'boolean';
export const cssProperties = (): Validator<CSSProperties> => (arg) =>
  typeof arg === 'object';
export const string = (): Validator<string> => (arg) => typeof arg === 'string';
export const literal =
  <T extends string>(str: T): Validator<T> =>
  (arg) =>
    arg === str;
export const number = (): Validator<number> => (arg) => typeof arg === 'number';
export const keyboardEventKeys = (): Validator<KeyboardEventKeys> => (arg) => {
  try {
    return (
      typeof arg === 'object' &&
      typeof arg.altKey === 'boolean' &&
      typeof arg.ctrlKey === 'boolean' &&
      typeof arg.metaKey === 'boolean' &&
      typeof arg.shiftKey === 'boolean' &&
      typeof arg.key === 'string'
    );
  } catch (ex) {
    return false;
  }
};

export const anyObject = (): Validator<object> => (arg) =>
  typeof arg === 'object';

// Please avoid nesting object validators
export const object =
  <T extends AnyObjectSchema>(
    validatorMap: T
  ): Validator<InferTypeFromSchema<T>> =>
  (arg) =>
    anyObject()(arg) &&
    // A missing key reads as `undefined`, which `optional(...)` accepts, so an
    // optional member may be omitted while required members still reject it.
    Object.entries(validatorMap).every(([key, validator]) =>
      validator((arg as Record<string, unknown>)[key])
    );

export const optional =
  <T>(validator: Validator<T>): Validator<Optional<T | undefined>> =>
  (arg: Optional<T | undefined>) =>
    arg === undefined || validator(arg);

export const arrayOf =
  <T>(validator: Validator<T>): Validator<T[]> =>
  (arg) =>
    Array.isArray(arg) && arg.every(validator);

export const or =
  <U extends ReadonlyArray<Validator<any>>>(
    ...validators: U
  ): Validator<InferArgType<ElementOf<U>>> =>
  (arg) =>
    validators.some((v) => v(arg));

export const and =
  <T extends ReadonlyArray<Validator<any>>>(
    ...validators: T
  ): Validator<Prettify<Conjunction<InferArgFromValidatorList<T>>>> =>
  (arg) =>
    validators.every((v) => v(arg));

export const empty = (): Validator<undefined> => (arg) =>
  typeof arg === 'undefined';

export const nullType = (): Validator<null> => (arg) => arg === null;

export const oneOf =
  <T extends (number | string)[]>(values: T): Validator<T[number]> =>
  (arg) =>
    values.includes(arg);

const voidFn = (): Validator<void> => (arg) => arg === undefined;
export { voidFn as void };

export function fn(): Validator<() => void>;
export function fn<R>(returnValidator: Validator<R>): Validator<() => R>;
export function fn<R, Args extends ReadonlyArray<Validator<any>>>(
  returnValidator: Validator<R>,
  ...argValidators: ValidateFnArgs<Args>
): Validator<(...args: InferFnArgs<Args>) => R>;
export function fn(...__validators: Validator<any>[]): Validator<any> {
  return (arg) => typeof arg === 'function';
}
